import { z } from "zod";

/** Normalized point on the original image, used to clear an enclosed area. */
export const backgroundPointSchema = z
  .object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) })
  .strict();
export type BackgroundPoint = z.infer<typeof backgroundPointSchema>;

export const backgroundRemovalModes = ["solid", "photo"] as const;
export type BackgroundRemovalMode = (typeof backgroundRemovalModes)[number];

/** What the server is asked to do. */
export const backgroundRemovalRequestSchema = z
  .object({
    mode: z.enum(backgroundRemovalModes).default("solid"),
    tolerance: z.number().int().min(1).max(100).default(24),
    points: z.array(backgroundPointSchema).max(24).default([]),
  })
  .strict();
export type BackgroundRemovalRequest = z.infer<
  typeof backgroundRemovalRequestSchema
>;

/** The stored method for each mode. The legacy browser method stays readable. */
export const backgroundRemovalMethods = {
  solid: "solid-colour-v1",
  photo: "u2netp-v1",
} as const satisfies Record<BackgroundRemovalMode, string>;

/** A retained transparent derivative; the enclosing assetId remains the original. */
export const backgroundRemovalSchema = z
  .object({
    version: z.literal(1),
    method: z.enum(["cutaway-isnet-quint8", "solid-colour-v1", "u2netp-v1"]),
    assetId: z
      .string()
      .max(200)
      .regex(/^asset_[a-zA-Z0-9_-]+$/),
    tolerance: z.number().int().min(1).max(100).optional(),
    points: z.array(backgroundPointSchema).max(24).optional(),
  })
  .strict();
export type BackgroundRemoval = z.infer<typeof backgroundRemovalSchema>;

export function backgroundRemoval(
  value: unknown,
): BackgroundRemoval | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return;
  const parsed = backgroundRemovalSchema.safeParse(
    (value as Record<string, unknown>).backgroundRemoval,
  );
  return parsed.success ? parsed.data : undefined;
}
